// Component_Withdraw_Application.tsx
import Icons_Repository from '../Icons/Icons_Repository';
import type { SelectedSlotWithContext } from '../Pages/Candidate_Page1';
import { CancelInterviewSlot, CancelInterviewSlotExp, sendCancelEmail } from '../Requests/Cancel_Slot';
import './Component_Withdraw_Application.css';
import { useState, JSX } from 'react';
import { CancelInterviewSlotPanel } from '../Requests/Cancel_Slot_Panel';


interface WithdrawApplicationProps{
  slot: any | null;
  candidateEmail: string;
  setSelectedPage: (page: string) => void;
  setCancelledInterview: (val: boolean) => void;
  setShowWithdrawPopup: (val: boolean) => void;
  isExperienced: boolean;
  isCampus: boolean;
  isPanel?: boolean;
}

function Component_Withdraw_Application({
  slot,
  candidateEmail,
  setSelectedPage,
  setCancelledInterview,
  setShowWithdrawPopup,
  isExperienced,
  isCampus,
  isPanel = false,
}: WithdrawApplicationProps) {
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [reason, setReason] = useState<string>('');

  if (!slot?.pairing) {
    return null;
  }

  const dStart = new Date(slot.pairing.start_time);
  const dEnd = new Date(slot.pairing.end_time);
  const dateYMD = dStart.toLocaleDateString('en-CA', { timeZone: browserTimeZone });
  const startStr = dStart.toLocaleTimeString('en-US', {
    timeZone: browserTimeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const endStr = dEnd.toLocaleTimeString('en-US', {
    timeZone: browserTimeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const timeRange = `${startStr} – ${endStr}`;

  const getAbbreviation = () => {
    try {
      const abbr = new Date()
        .toLocaleTimeString('en-us', {
          timeZone: browserTimeZone,
          timeZoneName: 'short',
        })
        .split(' ')
        .pop();

      if (abbr && /^[A-Z]{2,4}$/.test(abbr)) {
        return abbr;
      }

      const knownZones: Record<string, string> = {
        'Asia/Kolkata': 'IST',
        'Asia/Calcutta': 'IST',
        'America/New_York': 'EST',
        'America/Los_Angeles': 'PST',
        'America/Chicago': 'CST',
        'America/Denver': 'MST',
        'Europe/London': 'GMT',
        'Europe/Paris': 'CET',
        'Europe/Berlin': 'CET',
        'Asia/Dubai': 'GST',
        'Asia/Tokyo': 'JST',
        'Australia/Sydney': 'AEDT',
        'Australia/Melbourne': 'AEDT',
      };

      return knownZones[browserTimeZone] || abbr || browserTimeZone;
    } catch {
      return 'UTC';
    }
  };

  const toLongDate = (ymd: string): JSX.Element | string => {
    if (!ymd) return ymd;

    const [year, month, day] = ymd.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const getOrdinalSuffix = (n: number): JSX.Element => {
      let suffix = 'th';
      if (n < 11 || n > 13) {
        switch (n % 10) {
          case 1:
            suffix = 'st';
            break;
          case 2:
            suffix = 'nd';
            break;
          case 3:
            suffix = 'rd';
            break;
        }
      }
      return <>{n}</>;
    };

    const weekday = weekdays[date.getDay()];
    const monthName = months[date.getMonth()];
    const dayWithSuffix = getOrdinalSuffix(date.getDate());

    return (
      <>
        {weekday}, {monthName} {dayWithSuffix}, {year}
      </>
    );
  };

  // NEW
  const handleCancelApplication = async () => {
    try {
      setLoadingCancel(true);
      if (isPanel) {
      await CancelInterviewSlotPanel(slot?.pairing?.interview_pairing_id,reason,slot,candidateEmail);
    } else if (!isExperienced) {
        await CancelInterviewSlot(candidateEmail, slot?.pairing?.interview_pairing_id, reason);
        try {
          await sendCancelEmail(slot, candidateEmail, reason, false, isCampus);
        } catch (emailError) {
          console.error('Failed to send cancellation email:', emailError);
        }
      } else {
        // Email is handled per-slot inside CancelInterviewSlotExp
        await CancelInterviewSlotExp(candidateEmail, slot?.pairing?.interview_pairing_id, reason, slot);
      }

      setCancelledInterview(true);
      setShowWithdrawPopup(false);
      setSelectedPage('cancel_page');
    } catch (e) {
      console.error('Cancel (withdraw) failed:', e);
    } finally {
      setLoadingCancel(false);
    }
  };

  return (
    <div className="withdraw-main-popup-container">
      <div className="withdraw-popup-card">
        <div className="withdraw-popup-header">
          <div className="withdraw-cancel-button-parent">
            <div
              className="close-button"
              onClick={() => {
                setShowWithdrawPopup(false);
              }}
            >
              <Icons_Repository iconName="x" />
            </div>
          </div>
        </div>

        <div className="withdraw-section">
          <div className="withdraw-text-container">
            <div className="withdraw-text">
              Your interview is currently scheduled for the following time slot:
            </div>
            <div className="withdraw-text-date">
              <span>
                ({toLongDate(dateYMD)} at {timeRange} {getAbbreviation()})
              </span>
            </div>

            <div className= "withdraw-confirmation-text">
                <div className="withdraw-confirmation-text1">
                  Are you sure you want to cancel your interview? 
                </div>
                <div className='withdraw-confirmation-text2'>
                  Once canceled, you will no longer be able to select a time slot, and this will withdraw you from the interview process.
                </div>
            </div>
            <textarea
              className="withdraw-reason"
              placeholder="Please specify the reason for withdrawing your application."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <div className="withdraw-buttons">
          <button
            className="withdraw-confirm-button"
            onClick={handleCancelApplication}
            disabled={loadingCancel || reason.trim().length === 0}
          >
            {loadingCancel ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <span>Cancelling...</span>
              </div>
            ) : (
              'Withdraw from Interview'
            )}
          </button>

          <button
            className="withdraw-no-button"
            onClick={() => setShowWithdrawPopup(false)}
            disabled={loadingCancel}
          >
            Go back
          </button>
        </div>
      </div>
    </div>
  );
}

export default Component_Withdraw_Application;
